import { CART_SOURCE, type CartPartial } from './cart-partial.js';
import {
  normalizeCurrency,
  parseItemQuantity,
  parseMoney,
} from './parse-money.js';

const TOTAL_SELECTORS = [
  '[data-cart-total]',
  '[data-cart-subtotal]',
  '.cart-total',
  '.cart-subtotal',
  '.order-total',
  '.cart__total',
  '#cart-total',
  '[class*="cart-total" i]',
  '[class*="order-total" i]',
  '[class*="cart_total" i]',
].join(', ');

const LINE_SELECTORS = [
  '[data-cart-item]',
  '.cart-item',
  '.cart__item',
  '.cart-row',
  'tr.cart_item',
  '.woocommerce-cart-form__cart-item',
].join(', ');

function currencyFromText(text: string): string | undefined {
  const match = text.match(/\b([A-Z]{3})\b/);
  if (match) return normalizeCurrency(match[1]);
  if (/₴|грн/i.test(text)) return 'UAH';
  if (/\$/.test(text)) return 'USD';
  if (/€/.test(text)) return 'EUR';
  if (/£/.test(text)) return 'GBP';
  if (/zł/i.test(text)) return 'PLN';
  return undefined;
}

function readTotal(el: Element): {
  cartValue?: number;
  currency?: string;
} {
  const attrTotal =
    el.getAttribute('data-cart-total') ??
    el.getAttribute('data-cart-subtotal') ??
    el.getAttribute('content');
  const cartValue =
    parseMoney(attrTotal) ?? parseMoney((el.textContent ?? '').trim());
  const currency =
    normalizeCurrency(el.getAttribute('data-currency')) ??
    currencyFromText((el.textContent ?? '').trim());
  return {
    ...(cartValue != null ? { cartValue } : {}),
    ...(currency ? { currency } : {}),
  };
}

/**
 * Aggregate cart totals / line count from the cart page DOM.
 * Call only when the page is classified as cart (or clearly a cart URL).
 */
export function collectCartPageDomPartial(
  doc: Document = document,
): CartPartial | null {
  const totalNodes = Array.from(doc.querySelectorAll(TOTAL_SELECTORS));
  let cartValue: number | undefined;
  let currency: string | undefined;

  for (const el of totalNodes) {
    const parsed = readTotal(el);
    if (parsed.cartValue != null && cartValue == null) {
      cartValue = parsed.cartValue;
    }
    if (parsed.currency && !currency) currency = parsed.currency;
    if (cartValue != null && currency) break;
  }

  const lines = doc.querySelectorAll(LINE_SELECTORS);
  let itemsCount: number | undefined;
  if (lines.length > 0) {
    let qtySum = 0;
    let anyQty = false;
    lines.forEach((line) => {
      const qtyEl = line.querySelector(
        'input[name*="quantity" i], [data-quantity], .quantity input, .qty',
      );
      const qty =
        parseItemQuantity(qtyEl?.getAttribute('value')) ??
        parseItemQuantity(qtyEl?.getAttribute('data-quantity')) ??
        parseItemQuantity((qtyEl?.textContent ?? '').trim());
      if (qty != null) {
        qtySum += qty;
        anyQty = true;
      } else {
        qtySum += 1;
      }
    });
    itemsCount = anyQty ? qtySum : lines.length;
  }

  if (cartValue == null && itemsCount == null && currency == null) return null;

  return {
    source: CART_SOURCE.cartPageDom,
    confidence: 'medium',
    ...(itemsCount != null ? { itemsCount } : {}),
    ...(cartValue != null ? { cartValue } : {}),
    ...(currency ? { currency } : {}),
    ...(itemsCount === 0 && cartValue === 0 ? { emptyExplicit: true } : {}),
  };
}
